import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// import UploadExcel from "../../../Components/UploadExcel";
import UploadLeads from "../../../Components/Leads/UploadLeads";
import PendingLeadReviews from "../../../Components/Leads/PendingLeadReviews";
import { Eye, LoaderCircle } from "lucide-react";
import SideLeadForm from "../../../Components/SideLeadForm";
import StatusBadge from "../../../Components/StatusBadge";
import { db } from "../../../firebase";
import { useAuthUser } from "../../../hooks/useAuthUser";
import {
  doc as firestoreDoc,
  where,
  QueryConstraint,
  writeBatch,
} from "firebase/firestore";
import ConfirmationDialog from "../../../Components/ConfirmationDialogs/ConfirmationDialogs";
import ActionDialog from "../../../Components/ConfirmationDialogs/ActionDialogs";
import { Trash2 } from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../toastmsg";
import Pagination from "../../../Components/DataView/Pagination";
import SearchBar from "../../../Components/DataView/Searchbar";
import { convertToDisplayDate } from "../../../utils/date";
import { transformRow } from "../../../utils/Leads/excelUpload";
import Filters from "../../../Components/Filters";
import { leadsFilterSchema } from "../../../Components/filterSchemas/leadsFilter";
import { useFirestorePagination } from "../../../hooks/useFirestorePagination";
import { useSearchBar } from "../../../hooks/useSearchBar";
import { useAssignees } from "../../../hooks/useAssignees";
import { useDelete } from "../../../hooks/useDelete";

interface Lead {
  ID?: string;
  createdDate?: string;
  name?: string;
  interestedCourses?: string;
  schoolName?: string;
  studentResult?: string;
  estimatedResult?: string;
  lastTouchedDate?: string;
  status?: string;
  assignee?: string;
  assigneeId?: string;
  leadSource?: string;
  qualification?: string;
  [key: string]: any;
}

const toListText = (value: unknown): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return typeof value === "string" ? value : "";
};

const getCourseFinderAnswers = (lead: Lead) =>
  lead.courseFinderResult?.answers || {};

const getLeadInterests = (lead: Lead): string => {
  const answers = getCourseFinderAnswers(lead);
  return (
    lead.interestedCourses ||
    toListText(answers.courseFinderInterests) ||
    toListText(lead.courseFinderInterests)
  );
};

const getLeadStudentResult = (lead: Lead): string =>
  lead.studentResult || getCourseFinderAnswers(lead).studentResult || "";

const getLeadEstimatedResult = (lead: Lead): string =>
  lead.estimatedResult || getCourseFinderAnswers(lead).estimatedResult || "";

const getLeadCourseFinderStatus = (lead: Lead): string => {
  const answers = getCourseFinderAnswers(lead);
  if (
    lead.courseFinderResult ||
    lead.courseFinderInterests?.length ||
    answers.courseFinderInterests?.length
  ) {
    return "Completed";
  }
  return "-";
};

const getLeadCareerTestStatus = (lead: Lead): string =>
  lead.careerTestResult ? "Completed" : "-";

const Leads = () => {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({
    leadSource: "",
    overseas: "",
    qualification: "",
    familyFinancials: "",
    status: "",
    assigneeId: "",
    studentResult: "",
    estimatedResult: "",
    lastTouchedDate: "",
  });
  const [showPopout, setShowPopout] = useState(false);
  const [sortField, setSortField] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(
    new Set(),
  );

  const itemsPerPage = 20;

  // Search mode state (moved up to avoid circular dependency)
  const [isSearchMode, setIsSearchMode] = useState(false);

  const {
    userRole,
    currentUserUid,
    currentUserDisplayName,
    branchUserIds,
    isLoading: authLoading,
  } = useAuthUser();

  // Consolidate user data fetching via hook
  const { assigneeOptions, usersMap, uidToDisplayName } = useAssignees({
    userRole,
    branchUserIds,
  });
  const navigate = useNavigate();

  // Logic dependencies for hooks
  const scopeConstraints = useMemo(() => {
    const constraints: QueryConstraint[] = [];

    if (
      userRole === "super admin" ||
      userRole === "admin" ||
      userRole === "IT"
    ) {
      // no constraints
    } else if (userRole === "Branch Manager") {
      if (branchUserIds.length > 0) {
        if (filters.assigneeId) {
          if (branchUserIds.includes(filters.assigneeId)) {
            constraints.push(where("assigneeId", "==", filters.assigneeId));
          } else {
            constraints.push(where("assigneeId", "==", "impossible_id"));
          }
        } else {
          constraints.push(where("assigneeId", "in", branchUserIds));
        }
      } else if (currentUserUid) {
        constraints.push(where("assigneeId", "==", currentUserUid));
      }
    } else if (
      userRole === "sales" ||
      userRole === "part-time sales" ||
      currentUserUid
    ) {
      constraints.push(where("assigneeId", "==", currentUserUid));
    }

    return constraints;
  }, [userRole, branchUserIds, currentUserUid, filters.assigneeId]);

  const clientFilters = useMemo(() => {
    const activeFilters: Record<string, string> = {};
    if (filters.status) activeFilters.status = filters.status;
    if (filters.leadSource) activeFilters.leadSource = filters.leadSource;
    if (filters.overseas) activeFilters.overseas = filters.overseas;
    if (filters.qualification)
      activeFilters.qualification = filters.qualification;
    if (filters.familyFinancials)
      activeFilters.familyFinancials = filters.familyFinancials;
    if (filters.assigneeId && userRole !== "Branch Manager")
      activeFilters.assigneeId = filters.assigneeId;
    if (filters.studentResult)
      activeFilters.studentResult = filters.studentResult;
    if (filters.estimatedResult)
      activeFilters.estimatedResult = filters.estimatedResult;
    if (filters.lastTouchedDate)
      activeFilters.lastTouchedDate = filters.lastTouchedDate;
    return activeFilters;
  }, [filters, userRole]);

  // Main Hooks
  const {
    items: currentLeads,
    isLoading: paginationLoading,
    currentPage,
    totalPages,
    totalDocs,
    showPage,
    refresh,
    updateItemInCache,
    setItems,
    setTotalDocs,
    setTotalPages,
    setCurrentPage,
    setIsLoading,
  } = useFirestorePagination<Lead>({
    collectionName: "leads",
    itemsPerPage,
    sortField,
    sortOrder,
    baseConstraints: scopeConstraints,
    filters: clientFilters,
    isSearchMode: isSearchMode,
    enabled: !!userRole && !authLoading,
  });

  const isLoading = paginationLoading || authLoading;

  const {
    searchBarRef,
    handleSearchResults,
    onLoading: onSearchLoading,
    clearSearch,
  } = useSearchBar(
    {
      setItems,
      setTotalDocs,
      setTotalPages,
      setCurrentPage,
      setIsLoading,
    },
    itemsPerPage,
    isSearchMode,
    setIsSearchMode,
  );

  // Check User Role and Assignee
  const location = useLocation();

  useEffect(() => {
    if (location.state?.filterStatus || location.state?.assigneeId) {
      setFilters((prev) => ({
        ...prev,
        status: location.state.filterStatus || prev.status,
        assigneeId: location.state.assigneeId || prev.assigneeId,
        lastTouchedDate: location.state.lastTouchedDate || prev.lastTouchedDate,
      }));
      clearSearch();
    }
  }, [location.state, clearSearch]);

  const filteredLeads = currentLeads;

  const handleSortHeaderClick = (field: string) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleAddLead = () => navigate("/leads/add-lead");

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const ids = currentLeads
        .map((l) => l.ID)
        .filter((id): id is string => id !== undefined);
      setSelectedLeadIds(new Set(ids));
    } else {
      setSelectedLeadIds(new Set());
    }
  };

  const handleSelectLead = (id: string, checked: boolean) => {
    setSelectedLeadIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setSelectedLeadIds(new Set());
  }, [currentPage, filters, sortField, sortOrder, isSearchMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const handleLeadUpdate = async (updatedLead: Lead) => {
    const leadId = updatedLead.ID || updatedLead.id;
    if (!leadId) return;
    updateItemInCache(updatedLead);
  };

  // delete leads
  const {
    showDeleteDialog,
    confirmDelete,
    cancelDelete,
    handleDelete: handleDeleteLead,
    itemToDelete: leadToDelete,
  } = useDelete<Lead>({
    collectionName: "leads",
    storageField: "uploadedResult",
    onSuccess: refresh,
  });

  const handleBulkUpdate = async (updates: {
    assigneeId?: string;
    assigneeName?: string;
    status?: string;
  }) => {
    if (selectedLeadIds.size === 0) return;

    try {
      const batch = writeBatch(db);
      selectedLeadIds.forEach((id) => {
        const leadRef = firestoreDoc(db, "leads", id);
        const updateData: any = {};
        if (updates.assigneeId) {
          updateData.assigneeId = updates.assigneeId;
          updateData.assignee = updates.assigneeName;
        }
        if (updates.status) {
          updateData.status = updates.status;
        }
        // updateData.lastTouchedDate = new Date().toISOString();

        batch.update(leadRef, updateData);
      });

      await batch.commit();
      showSuccessToast(`Updated ${selectedLeadIds.size} leads successfully`);
      await refresh();
      setSelectedLeadIds(new Set());
    } catch (error) {
      console.error("Failed to update leads:", error);
      showErrorToast("Failed to update leads");
      throw error; // Re-throw to let ActionDialog know it failed
    }
  };

  const truncateCourses = (text: string) => {
    if (!text) return "-";
    const words = text.trim().split(/\s+/); // split by any whitespace
    return words.length > 4 ? words.slice(0, 4).join(" ") + "..." : text;
  };

  // ✅ Handle Excel upload completion - refresh table to show new leads immediately
  const handleUploadComplete = async (result: {
    added: number;
    pendingReview?: number;
    protectedMatches?: number;
    //skipped: number;
    errors: { row: any; error: string }[];
  }) => {
    if (result.added > 0) {
      try {
        // Clear search mode if active to show normal listing
        clearSearch();

        // Refresh the hook
        await refresh();

        // Scroll to top to show the new leads immediately
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Error refreshing leads after upload:", error);
        showErrorToast(
          "Leads uploaded but failed to refresh table. Please refresh the page.",
        );
      }
    }
  };

  return (
    <div className="relative flex flex-col gap-2 pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Leads</h2>
        <div className="flex gap-2">
          {(userRole === "super admin" ||
            userRole === "admin" ||
            userRole === "IT") && <PendingLeadReviews onResolved={refresh} />}
          {/* {(userRole === "super admin" || userRole === "admin") && ( */}
          <UploadLeads
            collectionName="leads"
            transformRow={(row) => {
              const transformed = transformRow(row);

              const isAdminUser =
                userRole === "super admin" ||
                userRole === "admin" ||
                userRole === "IT";

              if (isAdminUser) {
                const assigneeFromExcel =
                  transformed.assignee?.toLowerCase().trim() || "";
                const matchedUid = usersMap.get(assigneeFromExcel);
                if (matchedUid) {
                  const displayName = uidToDisplayName.get(matchedUid);
                  transformed.assignee =
                    displayName || transformed.assignee || "";
                  transformed.assigneeId = matchedUid;
                } else {
                  transformed.assigneeId = "";
                }
              } else {
                // Non-admin users (sales, Branch Manager) → auto-assign to themselves
                transformed.assigneeId = currentUserUid || "";
                transformed.assignee = currentUserDisplayName || "";
              }

              // Only set initial status if not already set (Cloud Function will set "Duplicate" if needed)
              if (!transformed.status) {
                if (
                  !transformed.assignee ||
                  transformed.assignee.trim() === ""
                ) {
                  transformed.status = "Unassigned";
                } else {
                  transformed.status = "New";
                }
              }

              return transformed;
            }}
            buttonLabel="Upload Leads"
            onUploadComplete={handleUploadComplete}
          />
          {/* )} */}
          {/* {(userRole === "super admin" || userRole === "admin") && ( */}
          <button
            onClick={handleAddLead}
            disabled={uploadingExcel}
            className="w-32 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer"
          >
            Add Lead
          </button>
          {/* )} */}
        </div>
      </div>

      {/* Search */}
      {currentUserUid && (
        <SearchBar
          ref={searchBarRef}
          collectionName="leads"
          placeholder="Search Leads Name..."
          searchFields={[
            "name",
            "schoolName",
            "phoneNumber",
            "ic",
            "email",
            "interestedCourses",
            "campaign",
            "reason",
            "location",
          ]}
          baseConstraint={scopeConstraints}
          filters={clientFilters}
          onLoading={onSearchLoading}
          onResults={handleSearchResults}
        />
      )}

      {/* Filters  */}
      <Filters
        availableFilters={leadsFilterSchema}
        showAssigneeFilter={true}
        branchUserIds={branchUserIds}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={() =>
          setFilters({
            leadSource: "",
            overseas: "",
            qualification: "",
            familyFinancials: "",
            status: "",
            assigneeId: "",
            studentResult: "",
            estimatedResult: "",
            lastTouchedDate: "",
          })
        }
        userRole={userRole}
      />

      {selectedLeadIds.size > 0 && (
        <div className="flex items-center gap-4">
          <p className="text-sm">
            Selected{" "}
            <span className="font-semibold">{selectedLeadIds.size}</span> leads
          </p>
          <button
            onClick={() => setShowPopout(true)}
            className="text-sm bg-blue-600 text-white px-2 py-1 rounded-md hover:bg-blue-700 cursor-pointer flex items-center gap-1"
          >
            Actions
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative min-h-[60%] overflow-x-auto border rounded">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-300/50 cursor-not-allowed">
            <span className="font-semibold flex flex-col items-center">
              <LoaderCircle className="animate-spin" />
              Loading...
            </span>
          </div>
        )}
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-300">
              {(userRole === "super admin" ||
                userRole === "admin" ||
                userRole === "IT" ||
                userRole === "Branch Manager") && (
                <th className="p-1">
                  <input
                    type="checkbox"
                    checked={
                      currentLeads.length > 0 &&
                      currentLeads.every(
                        (l) => l.ID && selectedLeadIds.has(l.ID),
                      )
                    }
                    onChange={handleSelectAll}
                    className="cursor-pointer flex items-center"
                  />
                </th>
              )}
              <th className="p-1">No</th>
              {[
                { label: "Created Date", field: "createdDate" },
                { label: "Name", field: "name" },
                { label: "Interested Courses", field: "interestedCourses" },
                { label: "School Name", field: "schoolName" },
                { label: "Student Result", field: "studentResult" },
                { label: "Estimated Result", field: "estimatedResult" },
                { label: "Course Finder", field: "courseFinderResult" },
                { label: "Career Test", field: "careerTestResult" },
                { label: "Last Touched", field: "lastTouchedDate" },
                { label: "Status", field: "status" },
              ].map(({ label, field }) => {
                const isSortable =
                  field === "createdDate" || field === "lastTouchedDate";
                return (
                  <th
                    key={field}
                    className={`p-1 select-none ${
                      isSortable ? "cursor-pointer" : ""
                    }`}
                    onClick={() =>
                      isSortable ? handleSortHeaderClick(field) : undefined
                    }
                  >
                    {label}
                    {isSortable && sortField === field && (
                      <span className="ml-1">
                        {sortOrder === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                );
              })}
              {(userRole === "super admin" ||
                userRole === "admin" ||
                userRole === "IT" ||
                userRole === "Branch Manager") && (
                <th className="p-1 select-none">Assignee</th>
              )}
              {(userRole === "super admin" || userRole === "admin") && (
                <th className="p-2">Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {!currentLeads || currentLeads.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    11 +
                    (userRole === "super admin" ||
                    userRole === "admin" ||
                    userRole === "IT" ||
                    userRole === "Branch Manager"
                      ? 1
                      : 0) +
                    (userRole === "super admin" || userRole === "admin" ? 1 : 0)
                  }
                  className="p-1"
                >
                  No leads available.
                </td>
              </tr>
            ) : (
              currentLeads.map((lead: Lead, index: number) => (
                <tr
                  key={lead.ID || index}
                  className="border-b hover:bg-gray-100"
                >
                  {(userRole === "super admin" ||
                    userRole === "admin" ||
                    userRole === "IT" ||
                    userRole === "Branch Manager") && (
                    <td className="p-1">
                      <input
                        type="checkbox"
                        checked={lead.ID ? selectedLeadIds.has(lead.ID) : false}
                        onChange={(e) =>
                          lead.ID && handleSelectLead(lead.ID, e.target.checked)
                        }
                        className="cursor-pointer flex items-center"
                      />
                    </td>
                  )}
                  <td className="p-1">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>
                  <td className="p-1">
                    {convertToDisplayDate(lead.createdDate || "")}
                  </td>
                  <td className="p-1 flex items-center gap-2">
                    {lead.name}
                    <button
                      onClick={() => {
                        setSelectedLead(lead);
                        setShowForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                  <td className="p-1">
                    {truncateCourses(getLeadInterests(lead))}
                  </td>
                  <td className="p-1">{lead.schoolName}</td>
                  <td className="p-1">{getLeadStudentResult(lead) || "-"}</td>
                  <td className="p-1">{getLeadEstimatedResult(lead) || "-"}</td>
                  <td className="p-1">{getLeadCourseFinderStatus(lead)}</td>
                  <td className="p-1">{getLeadCareerTestStatus(lead)}</td>
                  <td className="p-1">
                    {convertToDisplayDate(lead.lastTouchedDate || "")}
                  </td>
                  <td className="p-1">
                    <StatusBadge status={lead.status || "New"} />
                  </td>
                  {(userRole === "super admin" ||
                    userRole === "admin" ||
                    userRole === "IT" ||
                    userRole === "Branch Manager") && (
                    <td className="p-1">{lead.assignee}</td>
                  )}
                  {(userRole === "super admin" || userRole === "admin") && (
                    <td className="p-1">
                      <button
                        onClick={() => {
                          confirmDelete(lead);
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Lead"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={(page) => showPage(page)}
      />

      {/* Side LeadForm */}
      {showForm && selectedLead && (
        <div
          className="fixed top-0 right-0 w-[500px] h-screen bg-white shadow-lg overflow-auto z-50"
          ref={formRef}
        >
          <SideLeadForm
            lead={selectedLead}
            userRole={userRole}
            currentUserDisplayName={currentUserDisplayName}
            authLoading={authLoading}
            assigneeOptions={assigneeOptions}
            onClose={() => setShowForm(false)}
            onSave={async (updatedLead) => {
              await handleLeadUpdate(updatedLead as Lead);
              // Update selectedLead if it's still the same lead being viewed
              const leadId = updatedLead.ID || updatedLead.id;
              if ((selectedLead.ID || selectedLead.id) === leadId) {
                setSelectedLead({ ...selectedLead, ...updatedLead });
              }
              setShowForm(false);
            }}
          />
        </div>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        icon={<Trash2 size={32} className="text-red-600" />}
        title="Delete Lead"
        message={`Are you sure you want to delete ${leadToDelete?.name}? This action cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={handleDeleteLead}
      />

      <ActionDialog
        isOpen={showPopout}
        onClose={() => setShowPopout(false)}
        title="Bulk Actions"
        selectedCount={selectedLeadIds.size}
        userRole={userRole}
        branchUserIds={branchUserIds}
        onConfirm={handleBulkUpdate}
      />
    </div>
  );
};

export default Leads;
